import { useCallback, useMemo } from "react";
import { useTaskStore, useContactStore, useDealStore, useDealTypeStore, useProjectStore, useUserStore, useSyncStore } from "@/stores/StoreProvider";
import type { CrmTask, CrmTaskRequest } from "@/types/crm";
import type { CrmTask as StoreCrmTask } from "@/stores/types";
import type { ContactStore } from "@/stores/ContactStore";
import type { DealStore } from "@/stores/DealStore";
import type { DealTypeStore } from "@/stores/DealTypeStore";
import type { ProjectStore } from "@/stores/ProjectStore";
import type { UserStore } from "@/stores/UserStore";
import { toast } from "sonner";
import { toastUndo } from "@/components/ui/undo-toast";

function enrichTask(
  t: StoreCrmTask,
  contactStore: ContactStore,
  dealStore: DealStore,
  dealTypeStore: DealTypeStore,
  projectStore: ProjectStore,
  userStore: UserStore,
): CrmTask {
  const deal = t.dealId ? dealStore.getById(t.dealId) : undefined;
  return {
    ...t,
    description: t.description ?? undefined,
    dueDate: t.dueDate ?? undefined,
    contactId: t.contactId ?? undefined,
    dealId: t.dealId ?? undefined,
    projectId: t.projectId ?? undefined,
    ownerId: t.ownerId ?? undefined,
    contactName: t.contactId ? contactStore.getById(t.contactId)?.name : undefined,
    dealTypeName: deal?.dealTypeId ? dealTypeStore.getById(deal.dealTypeId)?.name : undefined,
    projectName: t.projectId ? projectStore.getById(t.projectId)?.name : undefined,
    ownerName: t.ownerId ? userStore.getById(t.ownerId)?.displayName : undefined,
  };
}

export function useTasks(params?: { contactId?: string; dealId?: string; projectId?: string }) {
  const taskStore = useTaskStore();
  const contactStore = useContactStore();
  const dealStore = useDealStore();
  const dealTypeStore = useDealTypeStore();
  const projectStore = useProjectStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();

  let tasks = taskStore.all;
  if (params?.contactId) tasks = taskStore.getByContactId(params.contactId);
  else if (params?.dealId) tasks = taskStore.getByDealId(params.dealId);
  else if (params?.projectId) tasks = taskStore.getByProjectId(params.projectId);

  // enrichTask uses only O(1) getById lookups — depend only on the task list itself.
  // Related store maps are stable references; MobX observer components re-render
  // when store contents change, keeping displayed names up to date.
  const data = useMemo(() =>
    tasks.map(t => enrichTask(t, contactStore, dealStore, dealTypeStore, projectStore, userStore)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks]);
  return { data, isLoading: !syncStore.bootstrapComplete };
}

export function useTask(id: string) {
  const taskStore = useTaskStore();
  const contactStore = useContactStore();
  const dealStore = useDealStore();
  const dealTypeStore = useDealTypeStore();
  const projectStore = useProjectStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const raw = id ? taskStore.getById(id) : undefined;
  const data = useMemo(() =>
    raw ? enrichTask(raw, contactStore, dealStore, dealTypeStore, projectStore, userStore) : undefined,
    [raw]);
  return { data, isLoading: !data && !syncStore.bootstrapComplete };
}

export function useCreateTask() {
  const taskStore = useTaskStore();
  const create = async (data: CrmTaskRequest) => {
    try {
      taskStore.create(data);
      toast.success("Task created");
    } catch (error) {
      toast.error("Failed to create task");
      throw error;
    }
  };
  return { mutateAsync: create, mutate: (data: CrmTaskRequest) => { create(data); }, isPending: false };
}

export function useUpdateTask() {
  const taskStore = useTaskStore();
  const update = async ({ id, data }: { id: string; data: CrmTaskRequest }) => {
    try {
      taskStore.update(id, data);
      toast.success("Task updated");
    } catch (error) {
      toast.error("Failed to update task");
      throw error;
    }
  };
  return { mutateAsync: update, mutate: (args: { id: string; data: CrmTaskRequest }) => { update(args); }, isPending: false };
}

export function useDeleteTask() {
  const taskStore = useTaskStore();
  const syncStore = useSyncStore();
  const mutateAsync = useCallback(
    async ({ id, name }: { id: string; name?: string }) => {
      taskStore.delete(id);
      toastUndo(
        name ? `"${name}" deleted` : "Task deleted",
        () => {
          syncStore.cancelPendingMutation('task', 'delete', id);
          taskStore.restore(id);
        },
      );
    },
    [taskStore, syncStore]
  );
  const mutate = useCallback(
    (args: { id: string; name?: string }) => {
      void mutateAsync(args);
    },
    [mutateAsync]
  );

  return useMemo(
    () => ({ mutateAsync, mutate, isPending: false }),
    [mutateAsync, mutate]
  );
}
